import {
  type ParentTaskType,
  type SubTaskType,
  type TaskContext,
  type hfb,
} from "@repo/api";
import { LightMode, documentIcon, getIconByName } from "@repo/skapa";
import { hasItems } from "@repo/utils";
import { type SVGProps } from "react";

import { CustomColors, makeAlphaColor } from "../Variables";
import { type Settings, type TaskConfig } from "./SettingsProvider";

const CALENDAR_DEFAULT_ALPHA_VALUE = "0.2";

const DEFAULT_TASK_COLOR = {
  color: CustomColors.taskTask,
  borderColor: CustomColors.taskTask,
  textColor: LightMode.colourTextAndIcon1,
};

export const getTaskColor = (
  parentTaskType?: string,
  active?: boolean,
  subType?: string,
  configs?: TaskConfig[]
) => {
  if (!parentTaskType) {
    return {
      ...DEFAULT_TASK_COLOR,
      color: makeAlphaColor(
        DEFAULT_TASK_COLOR.color,
        CALENDAR_DEFAULT_ALPHA_VALUE
      ),
    };
  }

  const taskConfig = getTaskConfig(parentTaskType, subType, configs);

  if (subType) {
    return {
      color: makeAlphaColor(
        taskConfig?.subTaskConfig?.colour?.token ??
          taskConfig?.parentTaskConfig?.colour?.token ??
          DEFAULT_TASK_COLOR.color,
        CALENDAR_DEFAULT_ALPHA_VALUE
      ),
      borderColor:
        taskConfig?.subTaskConfig?.colour?.token ??
        taskConfig?.parentTaskConfig?.colour?.token ??
        DEFAULT_TASK_COLOR.borderColor,
      textColor: active
        ? LightMode.colourTextAndIcon1
        : LightMode.colourTextAndIcon3,
    };
  }

  return {
    color: makeAlphaColor(
      taskConfig?.parentTaskConfig?.colour?.token ?? DEFAULT_TASK_COLOR.color,
      CALENDAR_DEFAULT_ALPHA_VALUE
    ),
    borderColor:
      taskConfig?.parentTaskConfig?.colour?.token ??
      DEFAULT_TASK_COLOR.borderColor,
    textColor: active
      ? LightMode.colourTextAndIcon1
      : LightMode.colourTextAndIcon3,
  };
};

export const getTaskIcon = (
  parentTaskType?: ParentTaskType,
  subTaskType?: SubTaskType,
  configs?: TaskConfig[]
): ((prefix?: string) => SVGProps<SVGElement>[]) | undefined => {
  const taskConfig = getTaskConfig(parentTaskType, subTaskType, configs);

  let icon;
  if (parentTaskType && subTaskType) {
    icon = getIconByName(
      taskConfig?.subTaskConfig?.icon ?? taskConfig?.parentTaskConfig?.icon
    );
  }

  if (!icon && parentTaskType) {
    icon = getIconByName(taskConfig?.parentTaskConfig?.icon);
  }

  return icon ?? documentIcon;
};

export const getParentTasksForContext = (
  context: TaskContext,
  flags?: Settings["calendar"],
  configs?: TaskConfig[],
  taskHfb?: hfb
) => {
  return configs
    ? configs
        .filter((config) => {
          if (
            config.name_key === "appointment" &&
            !(flags?.drop_in || flags?.follow_up)
          ) {
            return false;
          }

          return config.context?.includes(context);
        })
        .filter((config) => config.enabled)
        .filter((config) =>
          taskHfb && hasItems(config.hfbs)
            ? config.hfbs?.includes(taskHfb)
            : true
        )
        .map((config) => config.name_key)
    : [];
};

export const getSubTasksForContext = (
  context: TaskContext | undefined,
  parentTaskType: ParentTaskType,
  flags?: Settings["calendar"],
  configs?: TaskConfig[],
  taskHfb?: hfb
) => {
  const taskConfig = getTaskConfig(parentTaskType, undefined, configs);

  const subs = taskConfig?.parentTaskConfig?.subTypes
    ?.filter((config) => {
      const taskContext =
        config.context ?? taskConfig?.parentTaskConfig?.context;

      if (config.name_key === "dropIn" && !flags?.drop_in) {
        return false;
      }
      if (config.name_key === "followUp" && !flags?.follow_up) {
        return false;
      }

      return context === undefined || taskContext?.includes(context);
    })
    .filter((config) => config.enabled)
    .filter((config) =>
      taskHfb && hasItems(config.hfbs) ? config.hfbs?.includes(taskHfb) : true
    )
    .map((config) => config.name_key);

  return subs ?? [];
};

export const getTaskConfig = (
  parentTaskType?: ParentTaskType,
  subTaskType?: SubTaskType,
  configs?: TaskConfig[]
) => {
  if (!parentTaskType || !configs) {
    return undefined;
  }

  const enabledConfig = configs?.filter((config) => config.enabled);

  const parentTaskConfig = enabledConfig?.find(
    (config) =>
      config.name_key === parentTaskType ||
      config.old_name_key === parentTaskType
  );

  const subTaskConfig = parentTaskConfig?.subTypes
    ?.filter((sub) => sub.enabled)
    .find(
      (sub) => sub.name_key === subTaskType || sub.old_name_key === subTaskType
    );

  return { parentTaskConfig, subTaskConfig };
};

export const isTaskInContext = (
  parentTaskType: ParentTaskType | undefined,
  subTaskType: string | undefined,
  context: TaskContext,
  configs?: TaskConfig[]
) => {
  const taskConfig = getTaskConfig(parentTaskType, subTaskType, configs);

  const subContext = taskConfig?.subTaskConfig?.context;

  if (subContext && subContext.length > 0) {
    return subContext.includes(context);
  }
  const parentContext = taskConfig?.parentTaskConfig?.context;

  if (parentContext && parentContext.length > 0) {
    return parentContext.includes(context);
  }

  return false;
};

export const getIsCompletable = (
  parentTaskType?: ParentTaskType,
  configs?: TaskConfig[]
) => {
  if (!parentTaskType) {
    return undefined;
  }

  const parentTask = configs?.find(
    (config) => config.name_key === parentTaskType
  );

  return parentTask?.isCompletable;
};

export const getTimeInMinutes = (
  parentTaskType?: ParentTaskType,
  subTaskType?: string,
  configs?: TaskConfig[]
) => {
  if (!parentTaskType || !subTaskType) {
    return undefined;
  }

  const taskConfig = getTaskConfig(parentTaskType, subTaskType, configs);

  return taskConfig?.subTaskConfig?.timeInMinutes;
};

export const getAllDayConfig = (
  parentTaskType?: ParentTaskType,
  subTaskType?: string,
  configs?: TaskConfig[]
) => {
  const taskConfig = getTaskConfig(parentTaskType, subTaskType, configs);

  if (!parentTaskType || !subTaskType) {
    return undefined;
  }

  return taskConfig?.subTaskConfig?.allDay ?? false;
};

export const isDueDateTask = (
  parentTaskType?: ParentTaskType,
  subTaskType?: string,
  configs?: TaskConfig[]
) => {
  const taskConfig = getTaskConfig(parentTaskType, subTaskType, configs);

  if (!parentTaskType || !subTaskType) {
    return false;
  }

  return taskConfig?.subTaskConfig?.isDueDateTask ?? false;
};

export const getIsDateRangeOnlyTask = (
  parentTaskType?: ParentTaskType,
  subTaskType?: string,
  configs?: TaskConfig[]
) => {
  const taskConfig = getTaskConfig(parentTaskType, subTaskType, configs);

  if (!parentTaskType || !subTaskType) {
    return false;
  }

  return taskConfig?.subTaskConfig?.isDateRangeOnlyTask ?? false;
};
